/**
 * <khaime-cart-page> Web Component
 *
 * Full-page or compact sidebar cart view. Shares the item-row renderer and
 * action dispatcher in cart-shared.ts with the drawer. Theme is inherited
 * from the merchant's bootstrap config — no per-element theme override.
 */

import { KhaimeBaseElement, BASE_STYLES, ComponentRegistry } from '../base';
import { CartManager } from '../../utils/cart-manager';
import { formatPrice } from '../../types';
import { ADAPTIVE_TOKENS } from '../theme';
import {
  CART_SHARED_STYLES,
  renderCartItemRow,
  renderCartEmptyState,
  handleCartAction,
} from './cart-shared';
import type { BootstrapResponse } from '../../types';

type CartPageDisplay = 'full' | 'mini';

export class KhaimeCartPageElement extends KhaimeBaseElement {
  static tagName = 'khaime-cart-page';

  private _cartManager: CartManager | null = null;
  private _currency: string = 'USD';
  private _cartUnsub: (() => void) | null = null;
  private _transactionFeeCents: number = 0;
  private _customerPaysFees: boolean = false;
  private _lastFeeKey: string = '';

  static get observedAttributes(): string[] {
    return [...super.observedAttributes, 'display', 'cart-url', 'store-url'];
  }

  protected getSDKType(): string {
    return 'storefront';
  }

  private get display(): CartPageDisplay {
    return this.getAttribute('display') === 'mini' ? 'mini' : 'full';
  }

  private get cartUrl(): string {
    return this.getAttribute('cart-url') || '/cart';
  }

  private get storeUrl(): string {
    return this.getAttribute('store-url') || '/';
  }

  protected async render(): Promise<void> {
    this.injectStyles(CART_PAGE_STYLES + CART_SHARED_STYLES);

    // Remove the base loading container
    const existingLoader = this.shadow.querySelector('.ks-container');
    if (existingLoader) existingLoader.remove();

    // Detect currency from bootstrap once
    if (this.api) {
      try {
        const bootstrap: BootstrapResponse = await this.api.bootstrap();
        if (bootstrap.detectedCurrency) {
          this._currency = bootstrap.detectedCurrency;
        }
      } catch {
        /* fall back to USD */
      }
    }

    // Wire cart manager
    if (this.api) {
      this._cartManager = CartManager.getInstance(this.merchantId, this.api);
      this._cartManager.setCurrency(this._currency);
      this._cartManager.hydrate().catch(() => {});
    }

    const wrapper = document.createElement('div');
    wrapper.className = `ks-cart-page ks-cart-page--${this.display}`;
    wrapper.setAttribute('part', 'container');
    this.shadow.appendChild(wrapper);

    this.renderBody();

    // Subscribe for live updates
    if (this._cartManager) {
      this._cartUnsub = this._cartManager.subscribe(() => this.renderBody());
    }

    // Single delegated click handler
    wrapper.addEventListener('click', (e) => this.onClick(e));
  }

  private renderBody(): void {
    const wrapper = this.shadow.querySelector('.ks-cart-page') as HTMLElement | null;
    if (!wrapper || !this._cartManager) return;

    const items = this._cartManager.getItems();
    const itemCount = this._cartManager.getItemCount();
    const subtotalMinor = this._cartManager.getSubtotalMinor();
    const currency = this._cartManager.getCurrency() || this._currency;
    // Global formatPrice expects major units — divide once at the boundary.
    const subtotalDisplay = formatPrice(subtotalMinor / 100, currency);

    if (this.display === 'mini') {
      wrapper.innerHTML = this.renderMini(items, itemCount, subtotalDisplay, currency);
    } else {
      wrapper.innerHTML = this.renderFull(items, itemCount, subtotalDisplay, currency);
    }

    // Refresh fee out-of-band when cart contents/currency change; a second
    // renderBody() call will pick up the new values.
    this.refreshTransactionFee(subtotalMinor, currency);
  }

  /**
   * Fetch Khaime's transaction fee for the current subtotal + currency.
   * Cached per (amount, currency) to avoid hitting the API on every cart tick.
   * Silent no-op on error.
   */
  private async refreshTransactionFee(subtotalCents: number, currency: string): Promise<void> {
    if (!this.api || !subtotalCents || subtotalCents <= 0) return;

    const key = `${subtotalCents}|${currency}`;
    if (key === this._lastFeeKey) return;
    this._lastFeeKey = key;

    try {
      const breakdown = await this.api.getFeeBreakdown(subtotalCents, currency);
      if (!breakdown) return;

      if (
        breakdown.customerPaysFees !== this._customerPaysFees ||
        breakdown.transactionFeeMinor !== this._transactionFeeCents
      ) {
        this._customerPaysFees = breakdown.customerPaysFees;
        this._transactionFeeCents = breakdown.transactionFeeMinor;
        // Re-render to reflect the new fee row. _lastFeeKey stays set so the
        // next renderBody() won't re-fetch the same amount/currency pair.
        this.renderBody();
      }
    } catch {
      /* silent — fee row just won't appear */
    }
  }

  private renderFull(
    items: ReturnType<NonNullable<typeof this._cartManager>['getItems']>,
    itemCount: number,
    subtotalDisplay: string,
    currency: string,
  ): string {
    if (items.length === 0) {
      return `
        <header class="ks-cart-page-header">
          <div class="ks-cart-page-eyebrow">Shopping bag</div>
          <h1 class="ks-cart-page-title">Your bag</h1>
          <span class="ks-cart-page-rule" aria-hidden="true"></span>
        </header>
        ${renderCartEmptyState({
          title: 'Nothing here yet',
          subtitle: 'Your selections will appear here once you add them.',
          ctaLabel: 'Continue shopping',
          ctaAction: 'continue',
        })}
      `;
    }

    const itemsHtml = items.map((item) => renderCartItemRow(item, { currency, variant: 'full' })).join('');
    const itemWord = itemCount === 1 ? 'item' : 'items';

    const showFee = this._customerPaysFees && this._transactionFeeCents > 0;
    const subtotalCents = this._cartManager ? this._cartManager.getSubtotalMinor() : 0;
    const estimatedTotalCents = showFee ? subtotalCents + this._transactionFeeCents : subtotalCents;
    const estimatedTotalDisplay = formatPrice(estimatedTotalCents / 100, currency);
    const feeDisplay = formatPrice(this._transactionFeeCents / 100, currency);

    return `
      <header class="ks-cart-page-header">
        <div class="ks-cart-page-eyebrow">Shopping bag · ${itemCount} ${itemWord}</div>
        <h1 class="ks-cart-page-title">Your bag</h1>
        <span class="ks-cart-page-rule" aria-hidden="true"></span>
      </header>
      <div class="ks-cart-page-layout">
        <div class="ks-cart-page-items">${itemsHtml}</div>
        <aside class="ks-cart-page-summary">
          <div class="ks-cart-page-summary-eyebrow">Order summary</div>
          <div class="ks-cart-page-summary-row">
            <span class="ks-cart-page-summary-label">Subtotal</span>
            <span class="ks-cart-page-summary-value">${subtotalDisplay}</span>
          </div>
          ${
            showFee
              ? `
          <div class="ks-cart-page-summary-row">
            <span class="ks-cart-page-summary-label">Transaction Fee</span>
            <span class="ks-cart-page-summary-value">${feeDisplay}</span>
          </div>
          `
              : ''
          }
          <div class="ks-cart-page-summary-row ks-cart-page-summary-row--muted">
            <span class="ks-cart-page-summary-label">Shipping</span>
            <span class="ks-cart-page-summary-placeholder">Calculated at checkout</span>
          </div>
          <div class="ks-cart-page-summary-row ks-cart-page-summary-row--muted">
            <span class="ks-cart-page-summary-label">Taxes</span>
            <span class="ks-cart-page-summary-placeholder">Calculated at checkout</span>
          </div>
          <span class="ks-cart-page-summary-rule" aria-hidden="true"></span>
          <div class="ks-cart-page-summary-row ks-cart-page-summary-row--total">
            <span class="ks-cart-page-summary-label-lg">Estimated total</span>
            <span class="ks-cart-page-summary-value-lg">${estimatedTotalDisplay}</span>
          </div>
          <button class="ks-cart-page-checkout-btn" data-action="checkout">
            <span>Proceed to checkout</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 6l6 6-6 6"/>
            </svg>
          </button>
          <button class="ks-cart-page-continue-btn" data-action="continue">Continue shopping</button>
          <div class="ks-cart-page-summary-trust">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 11a3 3 0 100-6 3 3 0 000 6zM7 21a5 5 0 0110 0M5 11V8a2 2 0 012-2M19 11V8a2 2 0 00-2-2"/>
            </svg>
            <span>Secure checkout</span>
          </div>
        </aside>
      </div>
    `;
  }

  private renderMini(
    items: ReturnType<NonNullable<typeof this._cartManager>['getItems']>,
    itemCount: number,
    subtotalDisplay: string,
    currency: string,
  ): string {
    if (items.length === 0) {
      return `
        <header class="ks-cart-mini-header">
          <span class="ks-cart-mini-title">Cart</span>
          <span class="ks-cart-mini-count">0</span>
        </header>
        ${renderCartEmptyState({ compact: true, title: 'Cart is empty' })}
      `;
    }

    const itemsHtml = items.map((item) => renderCartItemRow(item, { currency, variant: 'compact' })).join('');

    return `
      <header class="ks-cart-mini-header">
        <span class="ks-cart-mini-title">Cart</span>
        <span class="ks-cart-mini-count">${itemCount}</span>
      </header>
      <div class="ks-cart-mini-items">${itemsHtml}</div>
      <footer class="ks-cart-mini-footer">
        <div class="ks-cart-mini-subtotal">
          <span>Subtotal</span>
          <span>${subtotalDisplay}</span>
        </div>
        <button class="ks-cart-mini-view-btn" data-action="view-cart">View cart</button>
      </footer>
    `;
  }

  private onClick(e: Event): void {
    if (!this._cartManager) return;
    const action = handleCartAction(e, this._cartManager);
    if (!action) return;

    switch (action) {
      case 'checkout':
        this.openCheckout();
        break;
      case 'continue':
        if (this.storeUrl) window.location.assign(this.storeUrl);
        break;
      case 'view-cart':
        if (this.cartUrl) window.location.assign(this.cartUrl);
        break;
    }
  }

  private async openCheckout(): Promise<void> {
    if (!this.api || !this._cartManager) return;
    if (this._cartManager.getItemCount() === 0) return;

    const { openCartCheckoutModal } = await import('../checkout/cart-checkout-modal');

    openCartCheckoutModal({
      api: this.api,
      cartManager: this._cartManager,
      merchantId: this.merchantId,
      currency: this._currency,
      onSuccess: (result) => {
        this.dispatchEvent(
          new CustomEvent('khaime:payment-success', {
            detail: {
              sessionToken: result.sessionToken,
              orderId: result.orderId,
              amount: result.amount,
              currency: result.currency,
              merchantId: this.merchantId,
              timestamp: Date.now(),
            },
            bubbles: true,
            composed: true,
          }),
        );
      },
      onError: (error) => {
        this.dispatchEvent(
          new CustomEvent('khaime:payment-error', {
            detail: {
              error: error.message,
              merchantId: this.merchantId,
              timestamp: Date.now(),
            },
            bubbles: true,
            composed: true,
          }),
        );
      },
      onClose: () => {},
    });
  }

  disconnectedCallback(): void {
    this._cartUnsub?.();
    this._cartUnsub = null;
    super.disconnectedCallback?.();
  }
}

const CART_PAGE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Manrope:wght@400;500;600;700&display=swap');

  :host {
    ${ADAPTIVE_TOKENS}

    display: block;
    opacity: 1 !important;
    color: inherit;
    font-family: var(--ksp-font-body);
    font-size: 15px;
    line-height: 1.55;
    letter-spacing: -0.003em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    background: transparent;
  }

  .ks-cart-page {
    display: block;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 56px 32px 96px;
    box-sizing: border-box;
    color: inherit;
    animation: kc-page-in 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
    container-type: inline-size;
    container-name: kc-page;
  }

  @keyframes kc-page-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @container kc-page (max-width: 720px) {
    .ks-cart-page { padding: 32px 20px 64px; }
  }

  .ks-cart-page--mini {
    padding: 0;
    max-width: 380px;
    background: var(--ksp-card-bg);
    border: 1px solid var(--ksp-hairline);
    border-radius: 4px;
    box-shadow: var(--ksp-shadow);
    overflow: hidden;
    animation: none;
  }

  /* ——— Header ——— */
  .ks-cart-page-header {
    margin-bottom: 40px;
    animation: kc-fade-up 560ms cubic-bezier(0.22, 0.61, 0.36, 1) 40ms both;
  }

  @keyframes kc-fade-up {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ks-cart-page-eyebrow {
    font-family: var(--ksp-font-body);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    /* Inherit host text color at 65% for a muted eyebrow that adapts
       to both light and dark WP themes. */
    color: inherit;
    opacity: 0.65;
    margin-bottom: 14px;
  }

  .ks-cart-page-title {
    font-family: var(--ksp-font-display);
    font-size: clamp(2.75rem, 6vw, 4.25rem);
    font-weight: 400;
    font-variation-settings: 'opsz' 144, 'SOFT' 30;
    line-height: 0.98;
    letter-spacing: -0.022em;
    /* Page-level heading — inherit WP text color */
    color: inherit;
    margin: 0 0 22px;
  }

  .ks-cart-page-rule {
    display: block;
    width: 48px;
    height: 2px;
    background: var(--ksp-accent);
    border-radius: 2px;
  }

  /* ——— Layout ——— */
  .ks-cart-page-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 56px;
    align-items: start;
  }

  /* Container queries — respond to the cart's own width, not the viewport.
     Necessary because WordPress themes constrain the content column; a
     media query would see the wide viewport and keep the 2-col layout,
     crushing the items column and overlapping the summary. */
  @container kc-page (max-width: 860px) {
    .ks-cart-page-layout {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .ks-cart-page-summary {
      position: static;
    }
  }

  @container kc-page (max-width: 520px) {
    .ks-cart-page-summary {
      padding: 24px 20px 20px;
    }
  }

  .ks-cart-page-items {
    /* Hairline that adapts to light/dark host backgrounds by using the
       inherited text color at low alpha. */
    border-top: 1px solid color-mix(in srgb, currentColor 14%, transparent);
    animation: kc-fade-up 620ms cubic-bezier(0.22, 0.61, 0.36, 1) 120ms both;
  }

  /* ——— Summary ——— */
  .ks-cart-page-summary {
    /* Adaptive card: a subtle tint of the inherited text color on
       transparent. On a light WP page with dark text → light-gray
       panel. On a dark WP page with light text → soft light panel.
       Reads correctly regardless of host theme or dashboard config. */
    background: color-mix(in srgb, currentColor 4%, transparent);
    color: inherit;
    border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
    border-radius: var(--ksp-radius-md);
    padding: 32px 28px 24px;
    position: sticky;
    top: 32px;
    animation: kc-fade-up 620ms cubic-bezier(0.22, 0.61, 0.36, 1) 200ms both;
  }

  .ks-cart-page-summary-eyebrow {
    font-family: var(--ksp-font-body);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ksp-muted);
    margin-bottom: 20px;
  }

  .ks-cart-page-summary-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    font-size: 0.9375rem;
    color: inherit;
    padding: 10px 0;
  }

  .ks-cart-page-summary-row--muted { color: var(--ksp-muted); }

  .ks-cart-page-summary-label {
    font-weight: 500;
    color: inherit;
  }

  .ks-cart-page-summary-row--muted .ks-cart-page-summary-label {
    color: var(--ksp-muted);
    font-weight: 500;
  }

  .ks-cart-page-summary-value {
    font-family: var(--ksp-font-display);
    font-size: 1rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 14;
    color: inherit;
    font-variant-numeric: tabular-nums;
  }

  .ks-cart-page-summary-placeholder {
    font-size: 0.8125rem;
    color: var(--ksp-muted-2);
    font-style: italic;
  }

  .ks-cart-page-summary-rule {
    display: block;
    height: 1px;
    background: var(--ksp-hairline);
    margin: 14px 0 8px;
  }

  .ks-cart-page-summary-row--total {
    padding: 8px 0 22px;
    align-items: baseline;
  }

  .ks-cart-page-summary-label-lg {
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: inherit;
  }

  .ks-cart-page-summary-value-lg {
    font-family: var(--ksp-font-display);
    font-size: 1.625rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 36;
    letter-spacing: -0.015em;
    color: inherit;
    font-variant-numeric: tabular-nums;
  }

  .ks-cart-page-checkout-btn {
    width: 100%;
    padding: 18px 22px;
    /* Dark CTA, morphs to merchant accent on hover. Matches the drawer
       checkout and empty-state CTA for a unified primary-action voice. */
    background: var(--ksp-cta-bg);
    color: var(--ksp-cta-ink);
    border: none;
    border-radius: var(--ksp-btn-radius);
    font-family: var(--ksp-font-body);
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    position: relative;
    overflow: hidden;
    transition: background 260ms ease, transform 160ms ease, letter-spacing 260ms ease;
  }

  .ks-cart-page-checkout-btn svg {
    width: 16px;
    height: 16px;
    transition: transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .ks-cart-page-checkout-btn:hover {
    background: var(--ksp-accent);
    letter-spacing: 0.2em;
  }

  .ks-cart-page-checkout-btn:hover svg { transform: translateX(4px); }
  .ks-cart-page-checkout-btn:active { transform: scale(0.985); }

  .ks-cart-page-continue-btn {
    margin-top: 10px;
    width: 100%;
    padding: 14px 22px;
    background: transparent;
    color: inherit;
    border: 1px solid var(--ksp-hairline);
    border-radius: var(--ksp-btn-radius);
    font-family: var(--ksp-font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: border-color 220ms ease, color 220ms ease, background 220ms ease;
  }

  .ks-cart-page-continue-btn:hover {
    border-color: inherit;
    color: inherit;
    background: var(--ksp-hairline-strong);
  }

  .ks-cart-page-summary-trust {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ksp-muted);
  }

  .ks-cart-page-summary-trust svg { width: 14px; height: 14px; }

  /* ——— Mini variant ——— */
  .ks-cart-mini-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--ksp-hairline);
  }

  .ks-cart-mini-title {
    font-family: var(--ksp-font-display);
    font-size: 1.125rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 24;
    color: inherit;
    letter-spacing: -0.01em;
  }

  .ks-cart-mini-count {
    font-family: var(--ksp-font-body);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
    border-radius: 999px;
    padding: 3px 10px;
    min-width: 22px;
    text-align: center;
  }

  .ks-cart-mini-items {
    max-height: 360px;
    overflow-y: auto;
    padding: 4px 20px;
    scrollbar-width: thin;
    scrollbar-color: var(--ksp-hairline) transparent;
  }

  .ks-cart-mini-footer {
    padding: 16px 20px 18px;
    border-top: 1px solid var(--ksp-hairline);
    background: var(--ksp-card-bg);
  }

  .ks-cart-mini-subtotal {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 0.875rem;
    color: inherit;
    margin-bottom: 14px;
  }

  .ks-cart-mini-subtotal > span:first-child {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ksp-muted);
  }

  .ks-cart-mini-subtotal > span:last-child {
    font-family: var(--ksp-font-display);
    font-size: 1.0625rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 24;
    font-variant-numeric: tabular-nums;
  }

  .ks-cart-mini-view-btn {
    width: 100%;
    padding: 13px 18px;
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
    border: none;
    border-radius: var(--ksp-btn-radius);
    font-family: var(--ksp-font-body);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 220ms ease, letter-spacing 220ms ease;
  }

  .ks-cart-mini-view-btn:hover {
    background: var(--ksp-accent);
    letter-spacing: 0.18em;
  }
`;

ComponentRegistry.define('khaime-cart-page', KhaimeCartPageElement);

export default KhaimeCartPageElement;
